import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import PrimaryDropdown from "../../../../../parts/primary-dropdown";
import SvgIcon from "../../../../../parts/SvgIcon";
import { getPhaseTemplateStatus } from "../../../../../utils/annual-planner-helpers";
import { openModal } from "../../../../../utils/modal-helpers";
import { PhaseTemplateDropdown } from "./PhaseTemplateDropdown";

export const Header = ({
  listView,
  setListView,
  selectedDaysState,
  setSelectedDaysState,
  phasesTemplates,
  athlete,
  athleteId,
  organizationCoachId,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);

  const openAddEventModal = () => {
    openModal("add-event", {
      eventFrom: selectedDaysState?.from
        ? moment(selectedDaysState.from).format("YYYYMMDD")
        : null,
      eventTo: selectedDaysState?.to
        ? moment(selectedDaysState.to).format("YYYYMMDD")
        : null,
    });
    setSelectedDaysState([]);
  };

  // Training and Nutrition phase both point at the only phase-creation flow
  // that exists today (the same one the grid's own "+" row header already
  // opens, see PhaseRowHeader.js) - there's no dual-track grid yet, but the
  // modal/API do now support a track param (see AddIndividualPhaseModal and
  // phase-track.js's resolveTrackField), so which menu item was clicked
  // decides which track the saved phase(s) land on.
  const openAddIndividualPhaseModal = (track) =>
    openModal("add-individual-phase", { track });

  const openAddGoalModal = () => openModal("manage-milestone-goals");

  const addMenuOptions = [
    { label: t("annualPlanner.trainingPhase"), value: "training-phase" },
    { label: t("annualPlanner.nutritionPhase"), value: "nutrition-phase" },
    { label: t("annualPlanner.event"), value: "event" },
    { label: t("annualPlanner.goal"), value: "goal" },
  ];

  const handleAddMenuSelect = (value) => {
    if (value === "training-phase")
      return openAddIndividualPhaseModal("training");
    if (value === "nutrition-phase")
      return openAddIndividualPhaseModal("nutrition");
    if (value === "event") return openAddEventModal();
    if (value === "goal") return openAddGoalModal();
  };

  const { templates, defaultTemplate } = phasesTemplates;

  const { currentTemplateId, isCurrentTheDefault } = getPhaseTemplateStatus({
    athlete,
    templates,
    defaultTemplate,
  });
  const hasDatedPhases = !!athlete?.annualPlan?.trainingPhases?.phases?.length;

  const openPhaseTemplateModal = () => {
    submit("/actions/refresh", {
      method: "GET",
      body: {
        "modal[type]": templates?.length
          ? "phase-templates"
          : "add-phase-template",
        eventFrom: selectedDaysState?.from
          ? moment(selectedDaysState.from).format("YYYYMMDD")
          : null,
        eventTo: selectedDaysState?.to
          ? moment(selectedDaysState.to).format("YYYYMMDD")
          : null,
      },
    });
  };

  return (
    <hgroup className="spaced">
      <div className="header-left-group">
        <h2>{t("annualPlanner.title")}</h2>
        <ul role="toggle-links" className="view-segmented">
          <a //NOSONAR
            role="button"
            data-button-type={!listView ? "outline" : "secondary"}
            className={!listView ? "active" : undefined}
            onClick={() => setListView(false)}
          >
            {t("annualPlanner.calendarView")}
          </a>
          <a //NOSONAR
            role="button"
            data-button-type={listView ? "outline" : "secondary"}
            className={listView ? "active" : undefined}
            onClick={() => setListView(true)}
          >
            {t("annualPlanner.listView")}
          </a>
        </ul>
      </div>

      <div className="header-right">
        <button data-button-type="secondary" onClick={openPhaseTemplateModal}>
          {t("annualPlanner.phaseTemplate")}
        </button>
        <PhaseTemplateDropdown
          templates={templates}
          currentTemplateId={currentTemplateId}
          isCurrentTheDefault={isCurrentTheDefault}
          athleteId={athleteId}
          organizationCoachId={organizationCoachId}
          hasDatedPhases={hasDatedPhases}
        />
        <PrimaryDropdown
          summaryText={t("common:add")}
          options={addMenuOptions}
          onSelectFn={handleAddMenuSelect}
          alignRight
        />
      </div>
    </hgroup>
  );
};

export const DateRangeController = ({
  dateFromState,
  setDateFromState,
  dateToState,
  setDateToState,
  keepRange = false,
}) => {
  const handleHeaderDates = (operation, amount, type) => {
    if (operation === "subtract") {
      setDateFromState(moment(dateFromState).subtract(amount, type));
      keepRange && setDateToState(moment(dateToState).subtract(amount, type));
    } else {
      keepRange && setDateFromState(moment(dateFromState).add(amount, type));
      setDateToState(moment(dateToState).add(amount, type));
    }
  };

  return (
    <div role="month-handler" //NOSONAR
    >
      <button
        type="button"
        data-button-type="secondary"
        data-icon-only
        onClick={() => handleHeaderDates("subtract", 1, "year")}
      >
        <SvgIcon src="/img/new-icons/double-arrow-left.svg" />
      </button>
      <div>
        <button
          type="button"
          data-button-type="text-white"
          data-icon-only
          onClick={() => handleHeaderDates("subtract", 1, "month")}
        >
          <SvgIcon src="/img/new-icons/arrow-left.svg" />
        </button>
        <span>
          {moment(dateFromState).format("MMM YYYY")} -{" "}
          {moment(dateToState).subtract(1, "month").format("MMM YYYY")}
        </span>
        <button
          type="button"
          data-button-type="text-white"
          data-icon-only
          onClick={() => handleHeaderDates("add", 1, "month")}
        >
          <SvgIcon src="/img/new-icons/arrow-right.svg" />
        </button>
      </div>
      <button
        type="button"
        data-button-type="secondary"
        data-icon-only
        onClick={() => handleHeaderDates("add", 1, "year")}
      >
        <SvgIcon src="/img/new-icons/double-arrow-right.svg" />
      </button>
    </div>
  );
};
